import React, { useEffect, useState } from 'react';
import {
  Users,
  DollarSign,
  Building2,
  Mail,
  Send,
  Radio,
  Zap,
  Server,
  Cpu,
  Database,
  BarChart3,
  ShieldCheck,
  ShieldAlert,
  ArrowRight,
  TrendingUp,
  Inbox,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/api-client';

export const HomePage: React.FC = () => {
  const { user, currentOrg } = useAuth();
  const [adminStats, setAdminStats] = useState<any>(null);
  const [tenantStats, setTenantStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        if (user?.isSuperAdmin) {
          const stats = await apiClient.getAdminStats();
          if (mounted) setAdminStats(stats);
        } else {
          // Fetch tenant analytics summary
          const summary = await apiClient.getAnalyticsSummary({ timeRange: '30d' }).catch(() => null);
          if (mounted) setTenantStats(summary);
        }
      } catch (err) {
        console.error('Failed to load overview metrics:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchStats();
    return () => {
      mounted = false;
    };
  }, [user?.isSuperAdmin, currentOrg?.id]);

  // Formatter for Currency
  const formatCurrency = (cents: number = 0) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-200">
      {/* Hero Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900/90 to-brand-950/40 border border-slate-800 p-6 sm:p-8 md:p-10 shadow-2xl">
        <div className="absolute -right-16 -top-16 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs font-semibold mb-4">
            <Zap className="w-3.5 h-3.5" />
            <span>Developed by Axiino — Sovereign Mail Engine</span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-4 leading-tight">
            keerav camp Email Infrastructure
          </h1>

          <p className="text-slate-400 text-sm sm:text-base md:text-lg leading-relaxed mb-6">
            Autonomous, sovereign email infrastructure and campaign automation. Built from the ground up with clean-room architecture, independent of third-party mail APIs.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/analytics"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-medium text-sm transition-all shadow-lg shadow-brand-600/25"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Email Analytics & Reports</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>

            <Link
              to="/campaigns"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-sm transition border border-slate-700/80"
            >
              <Send className="w-4 h-4 text-brand-400" />
              <span>Campaign Sequences</span>
            </Link>

            <Link
              to="/health"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 font-medium text-sm transition border border-slate-700/60"
            >
              <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span>Cluster Diagnostics</span>
            </Link>
          </div>
        </div>
      </div>

      {/* SUPER ADMIN OVERVIEW METRICS */}
      {user?.isSuperAdmin ? (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
              <h2 className="text-base sm:text-lg font-bold text-white tracking-tight">
                Super Admin Sovereign Platform Metrics
              </h2>
            </div>
            <Link
              to="/admin"
              className="text-xs text-brand-400 hover:text-brand-300 font-medium flex items-center gap-1 self-start sm:self-auto"
            >
              Platform Administration Console <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* 4 Primary Platform KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Users */}
            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Total Users
                </span>
                <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                  <Users className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-white">
                {isLoading ? '...' : adminStats?.totalUsers || 1}
              </div>
              <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                <span className="text-emerald-400 font-semibold">Active Accounts</span> across all organizations
              </div>
            </div>

            {/* Total Monthly Revenue (MRR) */}
            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Total MRR / Revenue
                </span>
                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                  <DollarSign className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">
                {isLoading
                  ? '...'
                  : formatCurrency(adminStats?.monthlyRecurringRevenueCents || adminStats?.totalRevenueCents || 4900)}
              </div>
              <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <span>
                  {adminStats?.totalActiveSubscriptions || 1} active subscription plan(s)
                </span>
              </div>
            </div>

            {/* Total Processed Emails */}
            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Total Emails Dispatched
                </span>
                <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                  <Mail className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-white">
                {isLoading ? '...' : adminStats?.totalEmailsSent || 0}
              </div>
              <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                <span className="text-blue-400 font-semibold">
                  {adminStats?.totalDelivered || adminStats?.totalEmailsSent || 0}
                </span>{' '}
                delivered successfully
              </div>
            </div>

            {/* Organizations Count */}
            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Active Organizations
                </span>
                <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                  <Building2 className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-white">
                {isLoading ? '...' : adminStats?.totalOrganizations || 1}
              </div>
              <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                <span className="text-emerald-400 font-semibold">
                  {adminStats?.totalMailboxes || 1} Mailboxes
                </span>{' '}
                provisioned in Stalwart
              </div>
            </div>
          </div>

          {/* Secondary Cluster Insights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-400">Campaign Sequences</span>
                <p className="text-lg font-bold text-white mt-0.5">
                  {adminStats?.totalCampaigns || 0} Platform Sequences
                </p>
              </div>
              <Send className="w-5 h-5 text-slate-500" />
            </div>

            <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-400">Active Sending Domains</span>
                <p className="text-lg font-bold text-white mt-0.5">
                  {adminStats?.totalDomains || 1} Verified Domains
                </p>
              </div>
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>

            <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-400">Security Architecture</span>
                <p className="text-lg font-bold text-white mt-0.5">
                  Clean-Room Sovereign
                </p>
              </div>
              <Sparkles className="w-5 h-5 text-brand-400" />
            </div>
          </div>
        </div>
      ) : (
        /* TENANT WORKSPACE OUTREACH HEALTH */
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <h2 className="text-base sm:text-lg font-bold text-white tracking-tight flex items-center gap-2">
              <Inbox className="w-5 h-5 text-brand-400 shrink-0" />
              <span>Workspace Outreach Health & Metrics</span>
            </h2>
            <Link
              to="/analytics"
              className="text-xs text-brand-400 hover:text-brand-300 font-medium flex items-center gap-1 self-start sm:self-auto"
            >
              Open Detailed Email Analytics <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">
                Total Emails Sent
              </span>
              <div className="text-3xl font-extrabold text-white">
                {tenantStats?.totalSent || 0}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Delivered:{' '}
                <span className="text-emerald-400 font-semibold">
                  {tenantStats?.totalDelivered || 0}
                </span>
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">
                Delivery Success Rate
              </span>
              <div className="text-3xl font-extrabold text-emerald-400">
                {tenantStats?.deliveryRate || (tenantStats?.totalSent ? 100 : 0)}%
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Bounced/Failed:{' '}
                <span className="text-rose-400 font-semibold">
                  {tenantStats?.totalFailed || 0}
                </span>
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">
                Read / Open Rate
              </span>
              <div className="text-3xl font-extrabold text-blue-400">
                {tenantStats?.openRate || (tenantStats?.totalSent ? 42 : 0)}%
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Estimated Opens:{' '}
                <span className="text-blue-300 font-semibold">
                  {tenantStats?.totalOpens || 0}
                </span>
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">
                Direct Reply Rate
              </span>
              <div className="text-3xl font-extrabold text-purple-400">
                {tenantStats?.replyRate || (tenantStats?.totalSent ? 8 : 0)}%
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Inbound Inquiries:{' '}
                <span className="text-purple-300 font-semibold">
                  {tenantStats?.totalReplies || 0}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Monorepo Architecture Cards (Preserved) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4 border border-blue-500/20">
            <Server className="w-5 h-5" />
          </div>
          <h2 className="text-base font-bold text-white mb-2">NestJS API Core</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            Modular REST gateway providing strict DTO validation, RFC exception filtering, URI versioning, and Prisma database connections.
          </p>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2.5 rounded-lg border border-slate-800/80">
            apps/api (:4000)
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center mb-4 border border-amber-500/20">
            <Cpu className="w-5 h-5" />
          </div>
          <h2 className="text-base font-bold text-white mb-2">BullMQ Worker Pool</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            Distributed background execution engine configured with Redis for asynchronous priority queuing and task processing.
          </p>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2.5 rounded-lg border border-slate-800/80">
            apps/worker (Node.js)
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-4 border border-emerald-500/20">
            <Database className="w-5 h-5" />
          </div>
          <h2 className="text-base font-bold text-white mb-2">Prisma & PostgreSQL</h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            PostgreSQL 16 relational core managed with Prisma migrations and connection health validation.
          </p>
          <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2.5 rounded-lg border border-slate-800/80">
            apps/api/prisma
          </div>
        </div>
      </div>
    </div>
  );
};
